import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Globe, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { SUPPORTED_LOCALES } from '../i18n';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

/**
 * Compact language switcher. Variants:
 *   - "menu-button"  (default): icon-only dropdown trigger, suitable for the
 *     top-right user/profile menu strip.
 *   - "inline":       full button row with current language label visible.
 *
 * Always persists the choice to the user's profile when authenticated.
 */
export function LanguageSwitcher({ variant = 'menu-button', testIdPrefix = 'lang' }) {
  const { t, i18n } = useTranslation();
  const { updateLocale, isAuthenticated } = useAuth();
  const [saving, setSaving] = useState(false);

  const current = i18n.language?.split('-')[0] || 'en';

  const handleChange = async (code) => {
    if (code === current || saving) return;
    setSaving(true);
    try {
      if (isAuthenticated) {
        await updateLocale(code);
      } else {
        await i18n.changeLanguage(code);
        try { localStorage.setItem('sl_locale', code); } catch (_) {}
      }
      const label = SUPPORTED_LOCALES.find((l) => l.code === code)?.label || code;
      toast.success(t('profile.languageSaved', { language: label }));
    } catch (err) {
      toast.error(t('profile.languageSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const currentLabel = SUPPORTED_LOCALES.find((l) => l.code === current)?.label || 'English';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant === 'inline' ? 'outline' : 'ghost'}
          size={variant === 'inline' ? 'default' : 'sm'}
          className={
            variant === 'menu-button'
              // Top-nav usage: force white contrast against the colored navbar
              // in both light and dark themes — without this, the Globe + locale
              // code render as default `text-foreground` which has near-zero
              // contrast against the gradient nav in dark mode.
              ? 'h-8 px-2 text-white hover:bg-white/10 hover:text-white focus-visible:text-white'
              : ''
          }
          data-testid={`${testIdPrefix}-switcher-trigger`}
          disabled={saving}
          aria-label={t('profile.language')}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Globe className="h-4 w-4" />
          )}
          {variant === 'inline' && <span className="ml-2">{currentLabel}</span>}
          {variant === 'menu-button' && (
            <span className="ml-1 text-xs font-medium uppercase tracking-wide">
              {current}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        <DropdownMenuLabel>{t('profile.language')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LOCALES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => handleChange(l.code)}
            data-testid={`${testIdPrefix}-option-${l.code}`}
            className="flex items-center justify-between gap-2"
          >
            <span>{l.label}</span>
            {current === l.code && <Check className="h-4 w-4 text-emerald-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSwitcher;
